// src/api/linkInBio.ts
//
// Authenticated API functions for link-in-bio management.
// Used by the main BandJerry app (builder, management, analytics).
// Public (unauthenticated) functions are in linkInBioPublic.ts.

import api from './index';
import type {
  LinkInBioPage,
  LinkInBioFormState,
  LinkInBioAnalytics,
  UploadUrlResponse,
  TrackableUrl,
} from '@/types/linkInBio';

// --- Authenticated endpoints ---

export async function getLinkInBioPage(): Promise<{ page: LinkInBioPage }> {
  const response = await api.get('/link-in-bio');
  return response.data;
}

export async function createLinkInBioPage(
  data: LinkInBioFormState,
): Promise<{ page: LinkInBioPage }> {
  const response = await api.post('/link-in-bio', data);
  return response.data;
}

export async function updateLinkInBioPage(
  data: Partial<LinkInBioFormState>,
): Promise<{ page: LinkInBioPage }> {
  const response = await api.patch('/link-in-bio', data);
  return response.data;
}

export async function deleteLinkInBioPage(): Promise<void> {
  await api.delete('/link-in-bio');
}

export async function getUploadUrl(
  filename: string,
  contentType: string,
): Promise<UploadUrlResponse> {
  const response = await api.post('/link-in-bio/upload-url', {
    filename,
    content_type: contentType,
  });
  return response.data;
}

export async function getLinkInBioAnalytics(
  range: '7d' | '30d' = '30d',
): Promise<LinkInBioAnalytics> {
  const response = await api.get('/link-in-bio/analytics', { params: { range } });
  return response.data;
}

export async function exportSignups(): Promise<Blob> {
  const response = await api.get('/link-in-bio/signups/export', {
    responseType: 'blob',
  });
  return response.data;
}

export async function getSignupsEmailList(): Promise<string> {
  const response = await api.get('/link-in-bio/signups/email-list', {
    responseType: 'text',
  });
  return response.data;
}

// --- Trackable URL endpoints ---

export async function createLinkInBioTrackableUrl(
  name: string,
): Promise<{ trackable_url: TrackableUrl }> {
  const response = await api.post('/link-in-bio/trackable-urls', { name });
  return response.data;
}

export async function deleteLinkInBioTrackableUrl(
  trackingId: string,
): Promise<void> {
  await api.delete(`/link-in-bio/trackable-urls/${trackingId}`);
}
